import DOMPurify from 'dompurify';
import { marked } from 'marked';

const SUGGESTIONS_PATTERN = /\n?\s*\[\[suggestions:([^\]]*)\]{1,2}\s*$/i;
const REFERENCED_EMAILS_PATTERN = /\n?\s*\[\[referenced_emails:([^\]]*)\]{1,2}\s*$/i;
const SCHEDULE_OFFER_PATTERN = /\n?\s*\[\[schedule_offer:([^\]]*)\]{1,2}\s*$/i;

export type ScheduleOffer = { question: string; accept: string; decline: string | null };

// Worki's proactive "want this as a recurring report?" offer, rendered as a banner with an accept
// button and a quiet dismiss (the design's frame 6) instead of plain suggestion pills
export const extractScheduleOffer = (content: string): ScheduleOffer | null => {
    const match = (content || '').match(SCHEDULE_OFFER_PATTERN);
    if (!match) return null;
    const [question, accept, decline] = match[1].split('|').map((part) => part.trim());
    if (!question || !accept) return null;
    return { question, accept, decline: decline || null };
};

const stripScheduleOffer = (content: string) => (content || '').replace(SCHEDULE_OFFER_PATTERN, '').trimEnd();

export const extractSuggestions = (content: string): string[] => {
    const match = (content || '').match(SUGGESTIONS_PATTERN);
    if (!match) return [];
    return match[1]
        .split('|')
        .map((option) => option.trim())
        .filter(Boolean)
        .slice(0, 4);
};

const OPTION_DETAIL_SEPARATOR = ' — ';

export const splitOption = (option: string): { label: string; detail: string | null } => {
    const index = option.indexOf(OPTION_DETAIL_SEPARATOR);
    if (index < 0) return { label: option, detail: null };
    return {
        label: option.slice(0, index).trim(),
        detail: option.slice(index + OPTION_DETAIL_SEPARATOR.length).trim() || null,
    };
};

export const optionMessage = (option: string) => {
    const { label, detail } = splitOption(option);
    return detail ? `${label}, ${detail}` : label;
};

export const hasDetailedOptions = (options: string[]) => options.some((option) => splitOption(option).detail);

export const extractReferencedEmailIds = (content: string): string[] | null => {
    const stripped = stripScheduleOffer(stripSuggestions(content || ''));
    const match = stripped.match(REFERENCED_EMAILS_PATTERN);
    if (!match) return null;
    return match[1]
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
};

export const stripSuggestions = (content: string) => (content || '').replace(SUGGESTIONS_PATTERN, '').trimEnd();

const stripReferencedEmails = (content: string) => (content || '').replace(REFERENCED_EMAILS_PATTERN, '').trimEnd();

export const stripAllMarkers = (content: string): string => {
    let result = content || '';
    let changed = true;
    while (changed) {
        const next = stripReferencedEmails(stripScheduleOffer(stripSuggestions(result)));
        changed = next !== result;
        result = next;
    }
    return result;
};

// charts arrive as fenced ```chart blocks containing a Flint spec (JSON); they render as chart cards
// below the text, so the blocks are stripped from the markdown
const CHART_BLOCK_PATTERN = /```chart\s*\n([\s\S]*?)```/g;
// while streaming, an unterminated chart block would flash as a plain code block until the fence closes
const UNTERMINATED_CHART_PATTERN = /```chart(?![\s\S]*?```)[\s\S]*$/;

// the compiler passes strings through to Chart.js, where "75%" becomes NaN and silently drops the
// point — coerce the one shape the model plausibly emits despite being told not to
const PERCENT_VALUE_PATTERN = /^-?\d+(\.\d+)?\s*%$/;

const coercePercentValues = (spec) => {
    const values = spec?.data?.values;
    if (!Array.isArray(values)) return spec;
    for (const row of values) {
        if (!row || typeof row !== 'object') continue;
        for (const [key, value] of Object.entries(row)) {
            if (typeof value === 'string' && PERCENT_VALUE_PATTERN.test(value)) row[key] = parseFloat(value);
        }
    }
    return spec;
};

export const chartImageFileName = (chartIndex: number) => `chart-${chartIndex + 1}.png`;
export const RENDERED_CHART_SOURCE = 'RENDERED_CHART';

export const extractCharts = (content: string): object[] => {
    const charts: object[] = [];
    for (const match of (content || '').matchAll(CHART_BLOCK_PATTERN)) {
        try {
            const spec = JSON.parse(match[1]);
            if (spec && typeof spec === 'object') charts.push(coercePercentValues(spec));
        } catch {
            // an invalid spec renders nothing rather than breaking the message
        }
    }
    return charts;
};

const stripChartBlocks = (content: string) =>
    (content || '').replace(CHART_BLOCK_PATTERN, '').replace(UNTERMINATED_CHART_PATTERN, '').trimEnd();

export const renderMarkdown = (content: string) => {
    const body = DOMPurify.sanitize(
        marked.parse(stripChartBlocks(stripAllMarkers(content)), { breaks: true, async: false }) as string,
        { RETURN_DOM: true }
    ) as HTMLElement;
    // Links in replies (e.g. workbench records) must not navigate the chat away.
    for (const link of body.querySelectorAll('a')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noreferrer');
    }
    return body.innerHTML;
};

// workbench record links mentioned in a reply get a dedicated open box below the text
const WORKBENCH_LINK_PATH_TYPES: Record<string, string> = {
    orders: 'ORDER',
    rfqs: 'REQUEST_FOR_QUOTATION',
    'order-confirmations': 'ORDER_CONFIRMATION',
    invoices: 'INVOICE',
    'delivery-notes': 'DELIVERY_NOTE',
};

const WORKBENCH_LINK_PATTERN =
    /(?:https?:\/\/[^\s)"'<>]*)?\/(orders|rfqs|order-confirmations|invoices|delivery-notes)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;

export const extractWorkbenchLinks = (content: string): { documentType: string; recordId: string }[] => {
    const links: { documentType: string; recordId: string }[] = [];
    const seen = new Set<string>();
    for (const match of (content || '').matchAll(WORKBENCH_LINK_PATTERN)) {
        const documentType = WORKBENCH_LINK_PATH_TYPES[match[1].toLowerCase()];
        const recordId = match[2].toLowerCase();
        const key = `${documentType}:${recordId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        links.push({ documentType, recordId });
    }
    return links;
};

export const MARKDOWN_STYLES = [
    '[&_p]:m-0 [&_p+p]:mt-2',
    '[&_ul]:m-0 [&_ul]:my-1 [&_ul]:pl-5 [&_ol]:m-0 [&_ol]:my-1 [&_ol]:pl-5 [&_li]:my-0.5',
    '[&_h1]:m-0 [&_h1]:mt-2 [&_h1]:text-sm [&_h1]:font-semibold',
    '[&_h2]:m-0 [&_h2]:mt-2 [&_h2]:text-sm [&_h2]:font-semibold',
    '[&_h3]:m-0 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold',
    '[&_code]:rounded [&_code]:bg-surface-strong [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs',
    '[&_pre]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-surface-strong [&_pre]:p-2',
    '[&_a]:text-content-brand-strong',
    '[&_table]:my-1 [&_table]:w-full [&_table]:border-collapse [&_th]:py-1 [&_th]:pr-3 [&_th]:text-left',
    '[&_td]:border-0 [&_td]:border-t [&_td]:border-solid [&_td]:border-line-weak [&_td]:py-1 [&_td]:pr-3',
    '[&_blockquote]:my-1 [&_blockquote]:border-0 [&_blockquote]:border-l-2 [&_blockquote]:border-solid [&_blockquote]:border-line-strong [&_blockquote]:pl-2 [&_blockquote]:text-content-medium',
].join(' ');
